import { TenantSelectionSection } from '@app/layouts/MainLayout/components/OutboundCall/OutboundCallModal/components/OutboundModalKeypad/TenantSelectionSection/TenantSelectionSection';
import CallingDisabled from '@components/CallingDisabledGuard/CallingDisabledGuard';
import { DYNAMIC_CALLER_ID_NUMBER } from '@components/OutboundCallerIdSelection/constants';
import { mainLayoutLocators } from '@locators/main-layout';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import useInboxState from '@modules/InboxModule/hooks/useInboxState';
import {
  Button,
  ButtonGroup,
  ButtonToggle,
  Modal,
  Stack,
} from '@servicetitan/design-system';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { getTenantId } from 'src/constants/tenantSelection';
import OutboundModalEmployeeTable from './components/OutboundModalEmployeeTable/OutboundModalEmployeeTable';
import OutboundModalKeypad from './components/OutboundModalKeypad/OutboundModalKeypad';
import OutboundModalQueues from './components/OutboundModalQueues';
import { initialCallDetails, useOutboundCallModalContext } from './context';
import styles from './OutboundCallModal.module.css';
import { OutboundCallsAllowedBanner } from './OutboundCallsAllowedBanner';
import { OutboundCallDetails, TabNameEnum, ITab } from './types';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import { shouldUseSoftPhone } from '@lib/utils/shouldUseSoftPhone';

export interface OutboundCallModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: string;
  actionButton?: {
    text: string;
    onClick: (data: OutboundCallDetails) => void;
    disabled?: boolean;
  };
  tabs?: ITab[];
  onClose?: () => void;
  animate?: boolean;
  id?: string;
  showTenantSelection?: boolean;
}

const initialTabs: ITab[] = [
  {
    value: TabNameEnum.Employees,
    selected: true,
  },
  {
    value: TabNameEnum.Queue,
  },
  {
    value: TabNameEnum.Keypad,
  },
];

const TabsToUseDID = [TabNameEnum.Employees, TabNameEnum.Queue];

/** Find the correct tab to keep in the component state */
const findSelectedTab = (tabs: ITab[]): TabNameEnum => {
  let selectedTab: ITab | undefined;
  let employeeTab: ITab | undefined;

  for (const tab of tabs) {
    if (tab.selected) {
      selectedTab = tab;
      break;
    }

    if (tab.value === 'Employees' && !tab.disabled) {
      employeeTab = tab;
    }
  }

  if (selectedTab) {
    return selectedTab.value;
  }

  if (employeeTab) {
    return employeeTab.value;
  }

  return TabNameEnum.Keypad;
};

/**
 * Returns a modal component that allow users to call employees, queues, or an external number.
 *
 * The modal consists of 3 "tabs": Employees, Queue, and Keypad, and each of them allows the user
 * to make a call in a different way.
 * - Employees: displays a list of employees that the user can call
 * - Queue: displays a list of queues that the user can call
 * - Keypad: the user can dial in a number and call it
 *
 * @param props: OutboundCallModalProps
 * @returns
 */
const OutboundCallModal: FC<OutboundCallModalProps> = ({
  open,
  setOpen,
  title = 'Make a Call',
  tabs = initialTabs,
  actionButton,
  onClose,
  animate = true,
  id,
  showTenantSelection = false,
}) => {
  const {
    callToDetails,
    callFromDetails,
    setCallToDetails,
    callButtonEnabled,
    outboundCallsAllowed,
    outboundCall,
    isProcessingCall,
  } = useOutboundCallModalContext();
  const {
    [FeatureFlagKeys.featureDisplayTenantDropdownForOutboundModal]:
      featureDisplayTenantDropdownForOutboundModal,
    [FeatureFlagKeys.fixOutboundCallerIdErrors]: fixOutboundCallerIdErrors,
  } = useFeatureFlags();
  const shouldShowTenantSelection =
    !!featureDisplayTenantDropdownForOutboundModal && showTenantSelection;

  const { user } = useAuth();
  const agentPersonalLine = user?.personalLine?.phoneNumber;
  const inboxState = useInboxState();
  const selectedTenantId = getTenantId(
    inboxState.selectedTenantForOutboundCall?.id ?? null,
  );

  const [selectedTab, setSelectedTab] = useState<TabNameEnum>(
    findSelectedTab(tabs),
  );

  const handleClose = useCallback(() => {
    setCallToDetails(initialCallDetails);
    setSelectedTab(TabNameEnum.Employees);

    setOpen(false);
  }, [setCallToDetails, setOpen]);

  const handleTabChange = (val: TabNameEnum) => {
    setCallToDetails(initialCallDetails);
    setSelectedTab(val);
  };

  const handleSetNumber = (number: string) => {
    setCallToDetails({ ...callToDetails, number });
  };

  const handlePrimaryButtonClick = () => {
    primaryButton.onClick();
    setCallToDetails((prev) => ({
      ...prev,
      entity: OutboundCallEntity[selectedTab],
    }));
  };

  const primaryButton = useMemo(() => {
    const handleCall = async () => {
      let callerNumber =
        callFromDetails.number === DYNAMIC_CALLER_ID_NUMBER
          ? null
          : callFromDetails.number;
      if (
        fixOutboundCallerIdErrors &&
        agentPersonalLine &&
        TabsToUseDID.includes(selectedTab)
      ) {
        callerNumber = agentPersonalLine;
      }
      await outboundCall({
        phoneNumber: callToDetails.number,
        customerName: callToDetails.callDisplayName,
        ...(!!featureDisplayTenantDropdownForOutboundModal && {
          callerNumber,
          // only pass tenantId if we are in keypad tab
          tenantId:
            selectedTab === TabNameEnum.Keypad ? selectedTenantId : undefined,
        }),
      });
      handleClose();
    };

    const onClick = () => {
      if (actionButton?.onClick) {
        actionButton.onClick(callToDetails);
      } else {
        handleCall();
      }
    };

    const hasTenantRequirementMet =
      !shouldShowTenantSelection ||
      !(selectedTab === TabNameEnum.Keypad) || // if not in keypad, no need to check for tenant
      // otherwise check for tenant selection
      (inboxState.selectedTenantForOutboundCall && user?.org.tenants.length);

    const isDisabled =
      actionButton?.disabled || // If the action button is explicitly disabled
      !callButtonEnabled || // If the call button is not enabled (e.g., missing required data)
      (!hasTenantRequirementMet && !shouldUseSoftPhone(callToDetails.number)) || // either tenant requirement met or it should call with softphone
      callFromDetails.loading; // If tenant change and from number is calculating

    return {
      text: actionButton?.text ?? 'Call',
      onClick: onClick,
      disabled: isDisabled,
    };
  }, [
    actionButton,
    callButtonEnabled,
    callToDetails,
    callFromDetails,
    handleClose,
    selectedTenantId,
    inboxState.selectedTenantForOutboundCall,
    user?.org.tenants,
    outboundCall,
    selectedTab,
    featureDisplayTenantDropdownForOutboundModal,
    fixOutboundCallerIdErrors,
    agentPersonalLine,
    shouldShowTenantSelection,
  ]);

  const tabOptions = useMemo(() => {
    return tabs.map((tab) => ({
      text: tab.value,
      value: tab.value,
      selected: selectedTab === tab.value,
      disabled: tab.disabled,
    }));
  }, [tabs, selectedTab]);

  return (
    <Modal
      className={styles.outboundCallModal}
      open={open}
      focusTrapOptions={{ disabled: true }}
      portal={true}
      title={title}
      onClose={onClose}
      animate={animate}
      id={id}
      footer={
        <ButtonGroup>
          <Button
            onClick={handleClose}
            id={mainLayoutLocators.outboundAndTransferModal.secondaryActionBtn}
            disabled={isProcessingCall}
          >
            Cancel
          </Button>
          <CallingDisabled additionalDisabled={primaryButton.disabled}>
            <Button
              primary
              id={mainLayoutLocators.outboundAndTransferModal.primaryActionBtn}
              onClick={handlePrimaryButtonClick}
              loading={isProcessingCall}
              disabled={primaryButton.disabled || isProcessingCall}
            >
              {primaryButton.text}
            </Button>
          </CallingDisabled>
        </ButtonGroup>
      }
    >
      <Stack direction='column' spacing={2}>
        <ButtonToggle
          className={styles.outboundCallModalTabs}
          onChange={handleTabChange}
          options={tabOptions}
        />

        {tabOptions.map((tab) => {
          if (!tab.selected) {
            return null;
          }
          switch (tab.value) {
            case 'Employees':
              return (
                <OutboundModalEmployeeTable
                  setCallDetails={setCallToDetails}
                  key={tab.value}
                />
              );
            case 'Queue':
              return (
                <OutboundModalQueues
                  setCallDetails={setCallToDetails}
                  key={tab.value}
                />
              );
            case 'Keypad':
              return (
                <Fragment key={tab.value}>
                  {!outboundCallsAllowed && <OutboundCallsAllowedBanner />}

                  {shouldShowTenantSelection && <TenantSelectionSection />}

                  <OutboundModalKeypad
                    number={callToDetails.number}
                    setNumber={handleSetNumber}
                  />
                  <div className={styles.keypadFooterSpace} />
                </Fragment>
              );
            default:
              return null;
          }
        })}
      </Stack>
    </Modal>
  );
};

export default OutboundCallModal;
